// R5.2 / R5.7 — LAYER 8: confidence (spec §4.8).
//
// "Review-level badge (`review_confidence`) + per-entry lines from `CONFIDENCE_COPY_IDS`, physically adjacent to
// the fact ledger." The review-level badge is a compact summary section (`ConfidenceBadge`); the per-entry line
// (`EntryConfidenceLine`) renders INSIDE the entry card, directly above that entry's facts disclosure — which is
// as physically adjacent to the fact ledger as a line can be.
//
// WHAT CONFIDENCE IS ABOUT, and the reason this component is so plain: `CS-CONF-*` describes how well we could
// READ the document, never the applicant ("We read this entry clearly" / "We had difficulty reading this").
// Rendering it as a scored badge — a colour, a meter, a percentage — would turn a statement about OUR extraction
// into a statement about THEIR CV. So it renders as a line of body text, like every other line.
//
// NOT A BAND, DELIBERATELY: confidence levels do not go through `BandChip`. §1.4's uniform contract governs BAND
// values (facets, domains, sub-elements, overlay documentation classes); a confidence level is not one of those
// ladders, and chipping it would invent a fourth ladder the codebook does not have.
//
// `rules_fired` never arrives here — it is deny-listed at projection ("Internal — never displayed"), so the
// served `ConfidenceDecision` is exactly `{level, copy_string_id}`.
//
// ─── R5.7 — THE DIRECTIONAL FIX (live smoke 2026-08-22, Claude+Sol ruling) ───────────────────────────────────
//
// The ratified `CS-CONF-MODERATE` ends "check the facts below". Layer 1 is not below Layer 8 — it never was in
// §4's fixed order, and in the R5.7 layout the entry's facts sit directly ABOVE/beside this line inside the same
// card. The ratified row cannot change (A.5 is exactly 28, byte-identical), so the PER-ENTRY line renders the
// provisional `CS-CONF-MODERATE-ENTRY` in its place — "… check this entry's facts." — and the review-level badge
// still renders the engine's own id untouched. The substitution is the ONLY id→id mapping on this surface, it is
// confined to this one table, and it maps a ratified id to a registered replacement, never to free text: the
// rendered string still resolves to an Appendix A id (T-COPY-1).
//
// AND A WAY THERE: moderate and low lines carry `CS-ENTRY-FACTS-LINK`, an in-page link that opens the entry's
// facts disclosure and moves focus to its summary. Its accessible name is the registered label plus the entry's
// own label, visually hidden — unique per entry, like every repeated control here.
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import type { CopyStringId } from './copy';
import { Copy, LayerHeading, ScreenReaderOnly, layerHeadingId } from './renderContent';
import { ledgerEntryDomId } from './FactLedgerPanel';
import { EntryLabel } from './EntryLabel';
import type { EntryHeading } from './evidenceIndex';
import { BODY_STYLE, MUTED_STYLE, SECTION_STYLE } from './styles';

type Entry = RubricReadPublicV1['entries'][number];
type Confidence = RubricReadPublicV1['review_confidence'];

/**
 * The per-entry directional substitution — the ONE id→id map on this surface. Keyed by the ratified id, valued by
 * its registered provisional replacement; any id not in this table renders as the engine stamped it.
 */
export const ENTRY_CONFIDENCE_COPY: Readonly<Partial<Record<CopyStringId, CopyStringId>>> = Object.freeze({
  'CS-CONF-MODERATE': 'CS-CONF-MODERATE-ENTRY',
});

export function entryConfidenceCopyId(id: CopyStringId): CopyStringId {
  return ENTRY_CONFIDENCE_COPY[id] ?? id;
}

/** The review-level badge: a compact summary section carrying the engine's own copy id, untouched. */
export function ConfidenceBadge({ reviewConfidence }: { reviewConfidence: Confidence }) {
  return (
    <section
      aria-labelledby={layerHeadingId('CS-SECTION-CONFIDENCE')}
      style={{ ...SECTION_STYLE, margin: '0 0 var(--space-4)' }}
      data-testid="rubric-layer-confidence"
    >
      <LayerHeading id="CS-SECTION-CONFIDENCE" />
      <p style={BODY_STYLE} data-testid="rubric-review-confidence" data-confidence-level={reviewConfidence.level}>
        <Copy id={reviewConfidence.copy_string_id} />
      </p>
    </section>
  );
}

/**
 * The per-entry line, inside the card. For a moderate or low reading it also offers the way to the facts.
 *
 * THE LINK OPENS THE DISCLOSURE THROUGH THE DOM. The facts `<details>` is uncontrolled (see `EntryFactsDisclosure`),
 * so setting `open` here does not fight React; focus then lands on its `<summary>`, which is the element a keyboard
 * user would otherwise have had to find by hand. The `href` is a plain fragment to the SYSTEM id, so the link still
 * works as a link with JavaScript out of the picture.
 */
export function EntryConfidenceLine({ entry, heading }: { entry: Entry; heading: EntryHeading | null }) {
  // 2026-08-23 — A CLEAN READ SAYS NOTHING (owner directive: "some things seem like you talking to yourself, not
  // the user"). `CS-CONF-HIGH` ("We read this entry clearly.") is the DEFAULT outcome, and printing it under every
  // entry of a normal CV is the product reporting on its own machinery once per entry — it tells the applicant
  // nothing to act on and buries the lines that do. The line now renders ONLY when there genuinely WAS trouble
  // reading the entry (moderate or low), which is the only case where it changes what the applicant should do:
  // check the facts. `CS-CONF-HIGH` stays REGISTERED, and the REVIEW-LEVEL badge above is untouched — §4.8's
  // requirement is that confidence be reported, and it is, once, for the read.
  if (entry.confidence.level === 'high') return null;
  const id = entryConfidenceCopyId(entry.confidence.copy_string_id);
  // Every line that still renders is a moderate or a low one, so every line still offers the way to the facts —
  // which is exactly what the link was always for. The condition is gone because the guard above subsumes it.
  const targetId = ledgerEntryDomId(entry.entry_id);
  return (
    <p
      style={{ ...BODY_STYLE, margin: 'var(--space-3) 0 0' }}
      data-testid="rubric-entry-confidence"
      data-confidence-level={entry.confidence.level}
    >
      <Copy id={id} />
      {(
        <>
          {' '}
          <a
            href={`#${targetId}`}
            style={{ ...MUTED_STYLE, margin: 0 }}
            data-testid="rubric-entry-facts-link"
            onClick={(e) => {
              const details = document.getElementById(targetId);
              if (!(details instanceof HTMLDetailsElement)) return;
              e.preventDefault();
              details.open = true;
              const summary = details.querySelector('summary');
              if (summary instanceof HTMLElement) summary.focus();
              if (typeof details.scrollIntoView === 'function') details.scrollIntoView({ block: 'nearest' });
            }}
          >
            <Copy id="CS-ENTRY-FACTS-LINK" />
            {heading !== null && (
              <ScreenReaderOnly>
                {' '}
                <EntryLabel heading={heading} as="span" />
              </ScreenReaderOnly>
            )}
          </a>
        </>
      )}
    </p>
  );
}
